import { onUnmounted, readonly, ref } from 'vue'
import { addBreadcrumb, captureError } from '@/utils'

/**
 * 轮询 Hook
 * @param callback 要轮询执行的函数
 * @param interval 轮询间隔时间（毫秒），默认 3000ms
 * @returns 包含开始和停止轮询方法的对象
 */
export function usePolling(
  callback: () => void | Promise<void>,
  interval: number = 3000
) {
  const timer = ref<NodeJS.Timeout | null>(null)
  const isPolling = ref(false)

  /**
   * 开始轮询
   */
  const startPolling = () => {
    // 如果已经在轮询中，先停止
    if (timer.value) {
      stopPolling()
    }

    isPolling.value = true
    timer.value = setInterval(async () => {
      try {
        await callback()
      } catch (error) {
        addBreadcrumb({
          message: '轮询任务执行失败',
          category: 'app/polling',
          level: 'error'
        })
        if (error instanceof Error) {
          captureError(error, { feature: 'usePolling' })
        }
      }
    }, interval)
  }

  /**
   * 停止轮询
   */
  const stopPolling = () => {
    if (timer.value) {
      clearInterval(timer.value)
      timer.value = null
    }
    isPolling.value = false
  }

  /**
   * 重新开始轮询（停止后重新开始）
   */
  const restartPolling = () => {
    stopPolling()
    startPolling()
  }

  // 组件卸载时自动清理定时器
  onUnmounted(() => {
    stopPolling()
  })

  return {
    isPolling: readonly(isPolling),
    startPolling,
    stopPolling,
    restartPolling
  }
}
